import { INodeResponse } from '@kcftech/prometheustypescriptapiclient';
import { SerialNumber } from 'components/hardware/SerialNumber';
import { joinNodes } from 'utils/joinNodes';

/**
 * Utility function to format serial number and name as a component.
 *
 * If a name is supplied, it will be included after the serial number with
 * parens.
 *
 * examples: "SN123", "SN123 (Test)"
 */
export const getHardwareDisplay = (
    serialNumber: string,
    name: string | undefined
): JSX.Element =>
    name?.trim() ? (
        <span>
            <SerialNumber value={serialNumber} /> ({name})
        </span>
    ) : (
        <SerialNumber value={serialNumber} />
    );

/**
 * Utility function to format serial number and name as a string.
 *
 * If a name is supplied, it will be included after the serial number with
 * parens.
 *
 * examples: "SN123", "SN123 (Test)"
 */
export const getHardwareDisplayString = (
    serialNumber: string,
    name: string | undefined
) => (name?.trim() ? `${serialNumber} (${name})` : serialNumber);

export type FormattableNode = Pick<INodeResponse, 'Nickname' | 'SerialNumber'> & {
    PortIndex?: number;
};

/**
 * Returns a formatted string for a given node as
 * `<Serial Number> (<Nickname>) (Port <PortIndex>)`
 * portindex being optional.
 * @param option The NodeResponse being displayed
 * @returns
 */
export const getNodeDropdownFormat = (option: FormattableNode | null) => {
    if (!option) {
        return undefined;
    }

    return (
        <span>
            {joinNodes(
                [
                    <SerialNumber
                        key={option.SerialNumber}
                        value={option.SerialNumber}
                    />,
                    !!option.Nickname && <>({option.Nickname})</>,
                    option.PortIndex !== undefined && <>(Port {option.PortIndex + 1})</>,
                ].filter(Boolean),
                <> </>
            )}
        </span>
    );
};

/**
 * Returns a formatted string for a given node as
 * `<Serial Number> (Port <PortIndex>)`
 * portindex being optional.
 * @param option The NodeResponse being displayed
 * @returns
 */
export const getNodeSerialNumberFormat = (
    option: Pick<FormattableNode, 'PortIndex' | 'SerialNumber'> | null
) => {
    if (!option) {
        return undefined;
    }

    let name = <SerialNumber value={option.SerialNumber} />;
    if (option.PortIndex !== undefined) {
        name = (
            <span>
                {name} (Port {option.PortIndex + 1})
            </span>
        );
    }
    return name;
};
